import * as React from 'react'
import CalendarIcon from '../../../assets/icons_new/Calendar.svg'
import DeleteIcon from '../../../assets/icons_new/DeleteIcon.svg'
import { createPortal } from 'react-dom'
import { FieldErrorMessage, FieldHelper, Input } from '../primitives'
import { useStableId } from '../hooks/useStableId'

import DateAndTimePickerPopup from '@/ui/date/DateAndTimePickerPopup'

// Example usage of DateField:
//
// <DateField
//   label="End Date"                             // The label displayed above the input
//   helper="The date when the card expires"      // Helper text shown below the input
//   error="Invalid date"                          // Error message displayed below the input in red
//   value={selectedDate}                          // Current date value in "DD-MM-YYYY" format
//   onChange={(e) => setSelectedDate(e)}          // Callback triggered when a date is selected; `e` is a "DD-MM-YYYY" string
// />
//
// Functionality:
// - Clicking the input opens the calendar (DatePickerSingle)
// - Selecting a date immediately updates the input
// - aria-describedby automatically links helper and error messages to the input for screen readers

const formatToDDMMYYYY = (value?: string | null) => {
  if (!value) return ''

  const timeMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]+(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/)
  if (timeMatch) {
    const year = timeMatch[1]
    const month = timeMatch[2]
    const day = timeMatch[3]
    const hour = timeMatch[4]
    const minute = timeMatch[5]
    return `${day}-${month}-${year} ${hour}:${minute}`
  }

  const d = new Date(value)
  if (isNaN(d.getTime())) return value
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
}

const parseDDMMYYYY = (input: string): string | null => {
  const trimmed = input.trim()

  const matchWithTime = trimmed.match(
    /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{2}):(\d{2})$/,
  )
  if (matchWithTime) {
    const day = parseInt(matchWithTime[1], 10)
    const month = parseInt(matchWithTime[2], 10)
    const year = parseInt(matchWithTime[3], 10)
    const hour = matchWithTime[4]
    const minute = matchWithTime[5]

    if (Number(hour) > 23 || Number(minute) > 59) return null
    if (month < 1 || month > 12) return null
    if (day < 1 || day > 31) return null
    if (year < 1900 || year > 2100) return null

    const date = new Date(year, month - 1, day)
    if (date.getMonth() !== month - 1 || date.getDate() !== day) return null

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${hour}:${minute}`
  }

  const match = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/)
  if (!match) return null

  const day = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const year = parseInt(match[3], 10)

  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  if (year < 1900 || year > 2100) return null

  const date = new Date(year, month - 1, day)
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} 00:00`
}
type DateFieldProps = React.ComponentProps<typeof Input> & {
  openDirection?: 'down' | 'up'
  setValue: (fullDate: string | null) => void
  label?: string
  placeholder?: string
  helper?: React.ReactNode
  error?: string | null
  confirmSelection?: boolean
}
export const DateAndTimeField: React.FC<DateFieldProps> = ({
  setValue,
  openDirection = 'down',
  label,
  value,
  error,
  disabled,
  id,
  required,
  helper,
  placeholder = 'DD-MM-RRRR GG:MM',
  // Bez potwierdzenia klik w dzień od razu zapisuje datę i zamyka popup — godzina przepada.
  confirmSelection = true,
  ...inputProps
}) => {
  const DROPDOWN_HEIGHT = 400

  const autoId = useStableId()
  const inputId = id ?? `autoid-${autoId}`
  const helperId = helper ? `${inputId}-helper` : undefined
  const errorId = error ? `${inputId}-error` : undefined

  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined
  const [open, setOpen] = React.useState(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const [inputValue, setInputValue] = React.useState('')

  React.useEffect(() => {
    setInputValue(formatToDDMMYYYY(value))
  }, [value])

  const handleCalendarIconClick = (e: React.MouseEvent) => {
    if (disabled) return
    e.stopPropagation()

    const willOpen = !open

    if (willOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()

      let top

      if (openDirection === 'down') {
        top = rect.bottom + window.scrollY + 4

        const spaceBelow = window.innerHeight - rect.bottom
        if (spaceBelow < DROPDOWN_HEIGHT) {
          top = rect.top + window.scrollY - DROPDOWN_HEIGHT - 4
        }
      } else {
        top = rect.top + window.scrollY - DROPDOWN_HEIGHT - 4

        if (rect.top < DROPDOWN_HEIGHT) {
          top = rect.bottom + window.scrollY + 4
        }
      }

      setDropdownPosition({
        top,
        left: rect.left + window.scrollX,
      })
    }

    setOpen(willOpen)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 12)

    const separators: Record<number, string> = { 2: '-', 4: '-', 8: ' ', 10: ':' }
    let formatted = ''
    for (let i = 0; i < digits.length; i++) {
      formatted += (separators[i] ?? '') + digits[i]
    }

    setInputValue(formatted)
  }

  const handleInputBlur = () => {
    const parsed = parseDDMMYYYY(inputValue)

    if (parsed) {
      setValue(parsed)
    } else if (inputValue === '') {
      setValue(null)
    } else {
      setInputValue(formatToDDMMYYYY(value))
    }
  }
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setValue(null)
    setOpen(false)
  }

  const handleConfirm = (fullDate: string) => {
    setValue(fullDate)
    setOpen(false)
  }
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div
      ref={containerRef}
      className="tw-relative tw-z-50 tw-inline-block tw-w-full"
    >
      {label && (
        <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}

      <div className="tw-relative tw-flex tw-items-center">
        <Input
          ref={inputRef}
          disabled={disabled}
          aria-describedby={describedBy}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          required={required}
          placeholder={placeholder}
          {...inputProps}
          className={`tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-leading-tight tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-400 data-[invalid=true]:tw-border-red-500 data-[invalid=true]:focus:tw-ring-red-500`}
        />
        <div className="tw-absolute tw-right-3 tw-flex tw-items-center tw-gap-2">
          {value && !disabled && (
            <img
              src={DeleteIcon}
              onClick={handleDeleteClick}
              alt="Delete Icon"
              className="tw-h-4 tw-w-4 tw-cursor-pointer hover:tw-opacity-70"
            />
          )}
          <img
            src={CalendarIcon}
            alt="Calendar"
            onClick={handleCalendarIconClick}
            className={`tw-h-4 tw-w-4 ${disabled ? 'tw-opacity-50' : 'tw-cursor-pointer'}`}
          />
        </div>
      </div>

      {helper && (
        <FieldHelper
          id={helperId}
          className="tw-mt-0.5 tw-text-xs tw-text-gray-500"
        >
          {helper}
        </FieldHelper>
      )}

      {error && (
        <FieldErrorMessage
          id={errorId}
          className="tw-mt-0.5 tw-text-xs tw-font-medium tw-text-red-600"
        >
          {error}
        </FieldErrorMessage>
      )}
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-absolute tw-z-[9999] tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <DateAndTimePickerPopup
              confirmSelection={confirmSelection}
              value={value ?? null}
              submit={handleConfirm}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}
